/**
 * Yandex Metrika / Yandex Direct helpers — goal reporting + capture of
 * tracking ids (ClientID and yclid) for offline conversion attribution.
 *
 * The captured ids are persisted to localStorage on first visit so they
 * survive refreshes / signup flows / messenger redirects, and POSTed to
 * `/user/track` once the user is authenticated so the YooKassa /
 * Stars-payment webhooks can attribute the conversion back to the ad
 * click.
 */

const YM_ID = Number(process.env.NEXT_PUBLIC_YM_ID) || 108345115;

const STORAGE_KEY_YCLID = "ym_yclid";
const STORAGE_KEY_CID = "ym_client_id";

export function ymReachGoal(goalName: string, params?: Record<string, unknown>) {
    try {
        const ym = (window as any).ym;
        if (typeof ym === "function") {
            ym(YM_ID, "reachGoal", goalName, params);
        }
    } catch {
        // Metrika not loaded, ignore
    }
}

export function ymGetClientId(): Promise<string | null> {
    return new Promise((resolve) => {
        try {
            const ym = (window as any).ym;
            if (typeof ym === "function") {
                ym(YM_ID, "getClientID", (clientId: string) => {
                    resolve(clientId || null);
                });
            } else {
                resolve(null);
            }
        } catch {
            resolve(null);
        }
    });
}

/** Read the stored yclid (set by `captureTrackingFromUrl`). Survives
 *  navigations and is what we forward on bot deep-links. */
export function getStoredYclid(): string | null {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage.getItem(STORAGE_KEY_YCLID);
    } catch {
        return null;
    }
}

/** Read the stored ym_client_id (set when first known). */
export function getStoredYmClientId(): string | null {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage.getItem(STORAGE_KEY_CID);
    } catch {
        return null;
    }
}

/** Parse `yclid` from the current URL on landing pages. Y.Direct adds
 *  this query param to every ad-click URL; we capture it once and stash
 *  it in localStorage so we can attach it to bot deep-links and the
 *  per-user `/user/track` POST after sign-up. Idempotent — never
 *  overwrites a previously captured yclid (the original ad click wins
 *  if a returning visitor lands organically later). */
export function captureTrackingFromUrl() {
    if (typeof window === "undefined") return;
    try {
        const url = new URL(window.location.href);
        const yclid = url.searchParams.get("yclid");
        if (yclid && !window.localStorage.getItem(STORAGE_KEY_YCLID)) {
            window.localStorage.setItem(STORAGE_KEY_YCLID, yclid);
        }
    } catch {
        // localStorage disabled / cross-origin — ignore.
    }
}

/** Resolve the Y.Metrika ClientID and persist it to localStorage so we
 *  can forward it on bot deep-links even after the Y.Metrika tag has
 *  been torn down (e.g. SPA navigation away from a pixel-mounting page). */
export async function captureYmClientId(): Promise<string | null> {
    if (typeof window === "undefined") return null;
    let cid = getStoredYmClientId();
    if (cid) return cid;
    cid = await ymGetClientId();
    if (cid) {
        try {
            window.localStorage.setItem(STORAGE_KEY_CID, cid);
        } catch {
            // ignore
        }
    }
    return cid;
}
